/**
 * CajaMovimientosPage - Registro y consulta de movimientos de efectivo
 * - Todo el efectivo se registra detallado por denominación (1¢ a $100)
 * - Billetes de $50 y $100 exigen fotografía, número de serie,
 *   factura y cliente que los entregó
 * - El usuario queda registrado automáticamente según su acceso
 */
import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AttachFile,
  Block,
  CameraAlt,
  Download,
  Visibility,
  Description,
  FilterList,
  Photo,
  PictureAsPdf,
  Print,
} from '@mui/icons-material';
import {
  PageHeader,
  LoadingSpinner,
  DataTable,
} from '../components/common';
import DenominacionesInput, {
  denomKey,
  calcularTotalCentavos,
  construirDetalle,
} from '../components/cajas/DenominacionesInput';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import * as cajaService from '../services/cajaService';
import { formatCurrency, formatDateTime } from '../utils/formatters';

const TIPO_LABELS = {
  ingreso: { label: 'Ingreso', color: 'success' },
  egreso: { label: 'Egreso', color: 'error' },
  ajuste: { label: 'Ajuste', color: 'warning' },
};

// Denominaciones que requieren registro individual con foto
const BILLETES_CONTROLADOS = [50, 100];

const FORM_INICIAL = {
  caja_id: '',
  tipo: 'egreso',
  categoria_id: '',
  monto: '',
  concepto: '',
  descripcion: '',
  beneficiario: '',
  numero_comprobante: '',
  direccion: 'incremento',
};

const CajaMovimientosPage = () => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [movimientos, setMovimientos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [catalogo, setCatalogo] = useState([]);

  // Filtros
  const [filtros, setFiltros] = useState({
    caja_id: '',
    tipo: '',
    estado: '',
    fecha_inicio: '',
    fecha_fin: '',
  });

  // Diálogo de registro
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState(FORM_INICIAL);
  const [cantidades, setCantidades] = useState({});
  const [billetes, setBilletes] = useState([]); // [{denominacion, numero_serie, factura, cliente, foto}]
  const [archivos, setArchivos] = useState([]);
  const [disponibles, setDisponibles] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // Detalle y anulación
  const [detalle, setDetalle] = useState(null);
  const [anular, setAnular] = useState(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');

  // Vista previa de las fotos de billetes: { [billeteId]: objectURL }
  const [fotosBilletes, setFotosBilletes] = useState({});
  const [fotoAmpliada, setFotoAmpliada] = useState(null); // { url, billete }

  const puedeRegistrar = ['administrador', 'contador', 'cajero'].includes(user?.rol);
  const puedeAnular = ['administrador', 'contador'].includes(user?.rol);
  const puedeAjustar = ['administrador', 'contador'].includes(user?.rol);

  // Solo cajas abiertas admiten movimientos; los cajeros solo la suya
  const cajasOperables = cajas.filter(
    (c) =>
      c.activa &&
      c.estado === 'abierta' &&
      (user?.rol !== 'cajero' || c.responsable_id === user?.id)
  );

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      Object.entries(filtros).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const [movData, cajasData, categoriasData] = await Promise.all([
        cajaService.getMovimientos({ ...params, limit: 200 }),
        cajaService.getCajas(),
        cajaService.getCategorias({ activa: true }),
      ]);
      setMovimientos(movData?.movimientos || []);
      setCajas(cajasData || []);
      setCategorias(categoriasData || []);
    } catch (error) {
      console.error('Error al cargar movimientos:', error);
      showError('Error al cargar los movimientos de caja');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filtros]);

  // Catálogo de denominaciones (una sola vez)
  useEffect(() => {
    cajaService
      .getCatalogoDenominaciones()
      .then(setCatalogo)
      .catch(() => showError('Error al cargar el catálogo de denominaciones'));
  }, []);

  // ---------- Cálculos derivados del formulario ----------

  // Los egresos/gastos se registran solo como transacción (sin conteo del dinero);
  // ingresos y ajustes exigen el conteo por denominación
  const esEgreso = form.tipo === 'egreso';
  const totalCentavos = calcularTotalCentavos(catalogo, cantidades);
  const montoTotal = esEgreso ? parseFloat(form.monto) || 0 : totalCentavos / 100;
  const entraEfectivo =
    form.tipo === 'ingreso' || (form.tipo === 'ajuste' && form.direccion === 'incremento');

  // Cantidad de billetes controlados según el conteo
  const billetesRequeridos = entraEfectivo
    ? BILLETES_CONTROLADOS.flatMap((valor) => {
        const qty = parseInt(cantidades[`billete-${valor.toFixed(2)}`], 10) || 0;
        return Array.from({ length: qty }, () => valor);
      })
    : [];

  // Sincronizar la lista de billetes con el conteo de denominaciones
  useEffect(() => {
    setBilletes((prev) => {
      const nuevos = billetesRequeridos.map((valor, index) => {
        const existente = prev.filter((b) => b.denominacion === valor)[
          billetesRequeridos.slice(0, index).filter((v) => v === valor).length
        ];
        return (
          existente || {
            denominacion: valor,
            numero_serie: '',
            factura: '',
            cliente: '',
            foto: null,
          }
        );
      });
      return nuevos;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(billetesRequeridos)]);

  // Cargar denominaciones disponibles de la caja para ajustes de disminución
  useEffect(() => {
    const saleEfectivo = form.tipo === 'ajuste' && form.direccion === 'decremento';
    if (openDialog && saleEfectivo && form.caja_id) {
      cajaService
        .getCajaDenominaciones(form.caja_id)
        .then((data) => setDisponibles(data.denominaciones))
        .catch(() => setDisponibles(null));
    } else {
      setDisponibles(null);
    }
  }, [openDialog, form.caja_id, form.tipo, form.direccion]);

  // ---------- Registro de movimiento ----------

  const handleOpenCreate = () => {
    setForm(FORM_INICIAL);
    setCantidades({});
    setBilletes([]);
    setArchivos([]);
    setOpenDialog(true);
  };

  const handleFormChange = (campo, valor) => {
    setForm((prev) => {
      const nuevo = { ...prev, [campo]: valor };
      if (campo === 'tipo') {
        nuevo.categoria_id = '';
      }
      return nuevo;
    });
  };

  const handleBilleteChange = (index, campo, valor) => {
    setBilletes((prev) => prev.map((b, i) => (i === index ? { ...b, [campo]: valor } : b)));
  };

  const handleArchivos = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length + archivos.length > 5) {
      showError('Solo se permiten hasta 5 documentos por movimiento');
      return;
    }
    setArchivos((prev) => [...prev, ...files]);
    event.target.value = '';
  };

  const handleQuitarArchivo = (index) => {
    setArchivos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGuardar = async () => {
    if (!form.caja_id || !form.concepto.trim()) {
      showError('Complete los campos requeridos: caja y concepto');
      return;
    }
    if (esEgreso) {
      if (!form.monto || parseFloat(form.monto) <= 0) {
        showError('Ingrese el monto del gasto (mayor a cero)');
        return;
      }
      if (!form.categoria_id) {
        showError('La categoría es obligatoria para egresos de dinero');
        return;
      }
      if (archivos.length === 0) {
        showError('Debe adjuntar al menos un documento de respaldo (factura, recibo, comprobante)');
        return;
      }
    } else if (totalCentavos <= 0) {
      showError('Debe contar el efectivo: ingrese las cantidades por denominación');
      return;
    }
    // Validar billetes controlados
    if (entraEfectivo) {
      for (const [i, billete] of billetes.entries()) {
        if (!billete.numero_serie.trim()) {
          showError(`Ingrese el número de serie del billete de $${billete.denominacion} (#${i + 1})`);
          return;
        }
        if (!billete.foto) {
          showError(`Adjunte la fotografía del billete de $${billete.denominacion} con serie ${billete.numero_serie}`);
          return;
        }
      }
    }

    try {
      setGuardando(true);
      const data = { ...form, monto: montoTotal.toFixed(2) };
      if (form.tipo !== 'ajuste') {
        delete data.direccion;
      }

      const detalleDenominaciones = esEgreso ? [] : construirDetalle(catalogo, cantidades);

      // Fotos en el mismo orden que los billetes (foto_index = posición)
      const fotosBilletes = billetes.map((b) => b.foto);
      const billetesData = billetes.map((b, index) => ({
        denominacion: b.denominacion,
        numero_serie: b.numero_serie.trim(),
        factura: b.factura.trim() || null,
        cliente: b.cliente.trim() || null,
        foto_index: index,
      }));

      await cajaService.createMovimiento(
        data,
        detalleDenominaciones,
        archivos,
        entraEfectivo ? billetesData : [],
        entraEfectivo ? fotosBilletes : []
      );
      showSuccess('Movimiento registrado correctamente');
      setOpenDialog(false);
      loadData();
    } catch (error) {
      console.error('Error al registrar movimiento:', error);
      showError(error.response?.data?.error || 'Error al registrar el movimiento');
    } finally {
      setGuardando(false);
    }
  };

  // ---------- Detalle / Documentos / Anulación ----------

  const handleVerDetalle = async (movimiento) => {
    try {
      const data = await cajaService.getMovimientoById(movimiento.id);
      setDetalle(data);
    } catch (error) {
      showError('Error al cargar el detalle del movimiento');
    }
  };

  // Cargar las vistas previas de las fotos de billetes al abrir el detalle;
  // liberar las URLs de objeto al cerrarlo para no fugar memoria
  useEffect(() => {
    if (!detalle?.billetes?.length) {
      return undefined;
    }
    let activo = true;
    const urls = [];
    (async () => {
      for (const billete of detalle.billetes) {
        try {
          const url = await cajaService.getBilleteFotoUrl(billete.id);
          if (!activo) {
            window.URL.revokeObjectURL(url);
            return;
          }
          urls.push(url);
          setFotosBilletes((prev) => ({ ...prev, [billete.id]: url }));
        } catch (error) {
          // Foto no disponible: se mantiene el ícono como respaldo
        }
      }
    })();
    return () => {
      activo = false;
      urls.forEach((url) => window.URL.revokeObjectURL(url));
      setFotosBilletes({});
      setFotoAmpliada(null);
    };
  }, [detalle?.id]);

  const handleDescargarDocumento = async (doc) => {
    try {
      await cajaService.downloadDocumento(doc.id, doc.nombre_original);
    } catch (error) {
      showError('Error al descargar el documento');
    }
  };

  // Comprobante PDF del movimiento (descargar / imprimir)
  const handleDescargarPdf = async (movimiento) => {
    try {
      await cajaService.downloadMovimientoPdf(movimiento.id);
    } catch (error) {
      showError(error.response?.data?.error || 'Error al descargar el comprobante PDF');
    }
  };

  const handleImprimirPdf = async (movimiento) => {
    try {
      await cajaService.imprimirMovimientoPdf(movimiento.id);
    } catch (error) {
      showError(error.response?.data?.error || 'Error al imprimir el comprobante PDF');
    }
  };

  const handleDescargarFotoBillete = async (billete) => {
    try {
      await cajaService.downloadBilleteFoto(billete.id, billete.foto_nombre_original);
    } catch (error) {
      showError('Error al descargar la foto del billete');
    }
  };

  const handleAnular = async () => {
    if (!motivoAnulacion.trim()) {
      showError('Debe indicar el motivo de la anulación');
      return;
    }
    try {
      await cajaService.anularMovimiento(anular.id, motivoAnulacion.trim());
      showSuccess('Movimiento anulado. El saldo y el desglose de la caja fueron revertidos.');
      setAnular(null);
      setMotivoAnulacion('');
      setDetalle(null);
      loadData();
    } catch (error) {
      console.error('Error al anular movimiento:', error);
      showError(error.response?.data?.error || 'Error al anular el movimiento');
    }
  };

  const categoriasFiltradas = categorias.filter(
    (c) => form.tipo === 'ajuste' || c.tipo === form.tipo
  );

  const columns = [
    {
      field: 'fecha_movimiento',
      headerName: 'Fecha',
      renderCell: (row) => formatDateTime(row.fecha_movimiento),
    },
    {
      field: 'caja.nombre',
      headerName: 'Caja',
      renderCell: (row) => row.caja?.nombre || '-',
    },
    {
      field: 'tipo',
      headerName: 'Tipo',
      renderCell: (row) => {
        const cfg = TIPO_LABELS[row.tipo] || { label: row.tipo, color: 'default' };
        return <Chip label={cfg.label} color={cfg.color} size="small" />;
      },
    },
    {
      field: 'categoria.nombre',
      headerName: 'Categoría',
      renderCell: (row) => row.categoria?.nombre || 'Sin categoría',
    },
    {
      field: 'concepto',
      headerName: 'Concepto',
    },
    {
      field: 'monto',
      headerName: 'Monto',
      renderCell: (row) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: row.tipo === 'egreso' ? 'error.main' : 'success.main',
          }}
        >
          {row.tipo === 'egreso' ? '-' : '+'}
          {formatCurrency(parseFloat(row.monto))}
        </Typography>
      ),
    },
    {
      field: 'saldo_nuevo',
      headerName: 'Saldo',
      renderCell: (row) => formatCurrency(parseFloat(row.saldo_nuevo)),
    },
    {
      field: 'usuario.nombre',
      headerName: 'Usuario',
      renderCell: (row) => row.usuario?.nombre || '-',
    },
    {
      field: 'documentos',
      headerName: 'Respaldo',
      searchable: false,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {row.documentos?.length > 0 && (
            <Chip
              icon={<AttachFile />}
              label={row.documentos.length}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
          {row.billetes?.length > 0 && (
            <Tooltip title={`${row.billetes.length} billete(s) de $50/$100 con foto`}>
              <Chip
                icon={<Photo />}
                label={row.billetes.length}
                size="small"
                color="warning"
                variant="outlined"
              />
            </Tooltip>
          )}
          {!row.documentos?.length && !row.billetes?.length && (
            <Chip label="Sin respaldo" size="small" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      renderCell: (row) => (
        <Chip
          label={row.estado === 'anulado' ? 'Anulado' : 'Activo'}
          color={row.estado === 'anulado' ? 'default' : 'success'}
          size="small"
        />
      ),
    },
  ];

  if (loading && movimientos.length === 0) {
    return <LoadingSpinner message="Cargando movimientos de caja..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Movimientos de Caja"
        breadcrumbs={[{ label: 'Cajas' }, { label: 'Movimientos' }]}
        actions={
          puedeRegistrar
            ? [
                {
                  label: 'Registrar Movimiento',
                  icon: AddIcon,
                  onClick: handleOpenCreate,
                  variant: 'contained',
                },
              ]
            : []
        }
      />

      {/* Filtros */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FilterList />
        <TextField
          select
          size="small"
          label="Caja"
          value={filtros.caja_id}
          onChange={(e) => setFiltros({ ...filtros, caja_id: e.target.value })}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {cajas.map((caja) => (
            <MenuItem key={caja.id} value={caja.id}>
              {caja.nombre}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Tipo"
          value={filtros.tipo}
          onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="ingreso">Ingreso</MenuItem>
          <MenuItem value="egreso">Egreso</MenuItem>
          <MenuItem value="ajuste">Ajuste</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Estado"
          value={filtros.estado}
          onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="activo">Activo</MenuItem>
          <MenuItem value="anulado">Anulado</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Desde"
          type="date"
          value={filtros.fecha_inicio}
          onChange={(e) => setFiltros({ ...filtros, fecha_inicio: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="Hasta"
          type="date"
          value={filtros.fecha_fin}
          onChange={(e) => setFiltros({ ...filtros, fecha_fin: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* Tabla de movimientos */}
      <DataTable
        columns={columns}
        data={movimientos}
        searchPlaceholder="Buscar por concepto, beneficiario..."
        emptyMessage="No hay movimientos registrados"
        renderCustomActions={(row) => (
          <>
            <Tooltip title="Ver detalle, desglose y documentos">
              <IconButton size="small" onClick={() => handleVerDetalle(row)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Descargar comprobante (PDF)">
              <IconButton size="small" color="error" onClick={() => handleDescargarPdf(row)}>
                <PictureAsPdf fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Imprimir comprobante">
              <IconButton size="small" color="primary" onClick={() => handleImprimirPdf(row)}>
                <Print fontSize="small" />
              </IconButton>
            </Tooltip>
            {puedeAnular && row.estado === 'activo' && (
              <Tooltip title="Anular movimiento">
                <IconButton size="small" color="error" onClick={() => setAnular(row)}>
                  <Block fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      />

      {/* ==================== Diálogo de registro ==================== */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Registrar Movimiento de Efectivo</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <ToggleButtonGroup
                value={form.tipo}
                exclusive
                onChange={(e, value) => value && handleFormChange('tipo', value)}
                size="small"
                color="primary"
              >
                <ToggleButton value="ingreso">Ingreso de Efectivo</ToggleButton>
                <ToggleButton value="egreso">Egreso / Gasto</ToggleButton>
                {puedeAjustar && <ToggleButton value="ajuste">Ajuste</ToggleButton>}
              </ToggleButtonGroup>
            </Grid>

            {form.tipo === 'ajuste' && (
              <Grid item xs={12}>
                <ToggleButtonGroup
                  value={form.direccion}
                  exclusive
                  onChange={(e, value) => value && handleFormChange('direccion', value)}
                  size="small"
                  color="warning"
                >
                  <ToggleButton value="incremento">Incrementar saldo</ToggleButton>
                  <ToggleButton value="decremento">Disminuir saldo</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Caja *"
                fullWidth
                value={form.caja_id}
                onChange={(e) => handleFormChange('caja_id', e.target.value)}
                helperText={
                  user?.rol === 'cajero' && cajasOperables.length === 0
                    ? 'No tiene una caja abierta hoy. Ábrala en el menú Cajas.'
                    : ''
                }
              >
                {cajasOperables.map((caja) => (
                  <MenuItem key={caja.id} value={caja.id}>
                    {caja.nombre} — Saldo: {formatCurrency(parseFloat(caja.saldo_actual))}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={form.tipo === 'egreso' ? 'Categoría *' : 'Categoría'}
                fullWidth
                value={form.categoria_id}
                onChange={(e) => handleFormChange('categoria_id', e.target.value)}
              >
                <MenuItem value="">Sin categoría</MenuItem>
                {categoriasFiltradas.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={esEgreso ? 4 : 6}>
              <TextField
                label="Concepto *"
                placeholder={esEgreso ? 'Ej: Pago movilización, Compra de agua' : ''}
                fullWidth
                value={form.concepto}
                onChange={(e) => handleFormChange('concepto', e.target.value)}
              />
            </Grid>

            {esEgreso && (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Monto del gasto *"
                  type="number"
                  inputProps={{ step: '0.01', min: '0.01' }}
                  fullWidth
                  value={form.monto}
                  onChange={(e) => handleFormChange('monto', e.target.value)}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={esEgreso ? 4 : 6}>
              <TextField
                label="N° de comprobante / factura"
                fullWidth
                value={form.numero_comprobante}
                onChange={(e) => handleFormChange('numero_comprobante', e.target.value)}
              />
            </Grid>

            {form.tipo === 'egreso' && (
              <Grid item xs={12}>
                <TextField
                  label="Beneficiario (quién recibe el dinero)"
                  fullWidth
                  value={form.beneficiario}
                  onChange={(e) => handleFormChange('beneficiario', e.target.value)}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Descripción adicional"
                fullWidth
                multiline
                rows={2}
                value={form.descripcion}
                onChange={(e) => handleFormChange('descripcion', e.target.value)}
              />
            </Grid>

            {/* ===== Conteo de efectivo por denominación (no aplica a egresos) ===== */}
            {!esEgreso && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Conteo de efectivo por denominación * (el monto se calcula automáticamente)
                </Typography>
                <DenominacionesInput
                  catalogo={catalogo}
                  cantidades={cantidades}
                  onChange={setCantidades}
                  disponibles={disponibles}
                />
              </Grid>
            )}

            {/* ===== Billetes de $50 y $100: foto + identificación ===== */}
            {entraEfectivo && billetes.length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 1 }} />
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Los billetes de $50 y $100 requieren fotografía, número de serie y
                  datos de identificación (factura y cliente que lo entregó).
                </Alert>
                {billetes.map((billete, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 1.5 }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Billete de ${billete.denominacion} — #{index + 1}
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            size="small"
                            label="Número de serie *"
                            fullWidth
                            value={billete.numero_serie}
                            onChange={(e) => handleBilleteChange(index, 'numero_serie', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            size="small"
                            label="Factura"
                            fullWidth
                            value={billete.factura}
                            onChange={(e) => handleBilleteChange(index, 'factura', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            size="small"
                            label="Cliente que entregó el billete"
                            fullWidth
                            value={billete.cliente}
                            onChange={(e) => handleBilleteChange(index, 'cliente', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                              component="label"
                              variant={billete.foto ? 'outlined' : 'contained'}
                              color={billete.foto ? 'success' : 'warning'}
                              size="small"
                              startIcon={<CameraAlt />}
                            >
                              {billete.foto ? 'Cambiar fotografía' : 'Adjuntar fotografía *'}
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                capture="environment"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleBilleteChange(index, 'foto', file);
                                  e.target.value = '';
                                }}
                              />
                            </Button>
                            {billete.foto && (
                              <Typography variant="caption" color="text.secondary">
                                {billete.foto.name} ({(billete.foto.size / 1024).toFixed(1)} KB)
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Grid>
            )}

            {/* ===== Documentos de respaldo ===== */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Documentos de respaldo{' '}
                {form.tipo === 'egreso' ? '(obligatorio para egresos)' : '(opcional)'}
              </Typography>
              <Button component="label" variant="outlined" startIcon={<AttachFile />} size="small">
                Adjuntar documentos
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                  onChange={handleArchivos}
                />
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                Hasta 5 archivos (PDF, imágenes, Word, Excel). Máx. 10 MB c/u.
              </Typography>
              <List dense>
                {archivos.map((file, index) => (
                  <ListItem
                    key={`${file.name}-${index}`}
                    secondaryAction={
                      <Button size="small" color="error" onClick={() => handleQuitarArchivo(index)}>
                        Quitar
                      </Button>
                    }
                  >
                    <ListItemIcon>
                      <Description fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024).toFixed(1)} KB`}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ mr: 'auto', fontWeight: 700 }}>
            Total: {formatCurrency(montoTotal)}
          </Typography>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== Diálogo de detalle ==================== */}
      <Dialog open={!!detalle} onClose={() => setDetalle(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalle del Movimiento #{detalle?.id}</DialogTitle>
        <DialogContent>
          {detalle && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Fecha:</strong> {formatDateTime(detalle.fecha_movimiento)}
              </Typography>
              <Typography variant="body2">
                <strong>Caja:</strong> {detalle.caja?.nombre}
              </Typography>
              <Typography variant="body2">
                <strong>Tipo:</strong> {TIPO_LABELS[detalle.tipo]?.label || detalle.tipo}
              </Typography>
              <Typography variant="body2">
                <strong>Categoría:</strong> {detalle.categoria?.nombre || 'Sin categoría'}
              </Typography>
              <Typography variant="body2">
                <strong>Concepto:</strong> {detalle.concepto}
              </Typography>
              {detalle.descripcion && (
                <Typography variant="body2">
                  <strong>Descripción:</strong> {detalle.descripcion}
                </Typography>
              )}
              {detalle.beneficiario && (
                <Typography variant="body2">
                  <strong>Beneficiario:</strong> {detalle.beneficiario}
                </Typography>
              )}
              {detalle.numero_comprobante && (
                <Typography variant="body2">
                  <strong>N° Comprobante:</strong> {detalle.numero_comprobante}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Monto:</strong> {formatCurrency(parseFloat(detalle.monto))}
              </Typography>
              <Typography variant="body2">
                <strong>Saldo anterior:</strong> {formatCurrency(parseFloat(detalle.saldo_anterior))}{' '}
                → <strong>Saldo nuevo:</strong> {formatCurrency(parseFloat(detalle.saldo_nuevo))}
              </Typography>
              <Typography variant="body2">
                <strong>Registrado por:</strong> {detalle.usuario?.nombre} ({detalle.usuario?.rol})
              </Typography>

              {detalle.estado === 'anulado' && (
                <Box sx={{ bgcolor: 'error.50', p: 1, borderRadius: 1 }}>
                  <Typography variant="body2" color="error">
                    <strong>ANULADO</strong> por {detalle.usuario_anula?.nombre} —{' '}
                    {formatDateTime(detalle.fecha_anulacion)}
                  </Typography>
                  <Typography variant="body2" color="error">
                    Motivo: {detalle.motivo_anulacion}
                  </Typography>
                </Box>
              )}

              {/* Desglose por denominación */}
              {detalle.denominaciones?.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">Desglose del efectivo</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Denominación</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Cantidad</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detalle.denominaciones.map((d) => (
                          <TableRow key={d.id}>
                            <TableCell>{formatCurrency(parseFloat(d.denominacion))}</TableCell>
                            <TableCell>{d.tipo === 'moneda' ? 'Moneda' : 'Billete'}</TableCell>
                            <TableCell align="center">{d.cantidad}</TableCell>
                            <TableCell align="right">{formatCurrency(parseFloat(d.subtotal))}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Billetes de $50/$100 */}
              {detalle.billetes?.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">
                    Billetes de $50/$100 registrados ({detalle.billetes.length})
                  </Typography>
                  <List dense>
                    {detalle.billetes.map((billete) => (
                      <ListItem
                        key={billete.id}
                        secondaryAction={
                          <>
                            {fotosBilletes[billete.id] && (
                              <Tooltip title="Ver fotografía">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setFotoAmpliada({ url: fotosBilletes[billete.id], billete })
                                  }
                                >
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Descargar fotografía">
                              <IconButton size="small" onClick={() => handleDescargarFotoBillete(billete)}>
                                <Download fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 84 }}>
                          {fotosBilletes[billete.id] ? (
                            <Tooltip title="Ver fotografía">
                              <Box
                                component="img"
                                src={fotosBilletes[billete.id]}
                                alt={`Billete serie ${billete.numero_serie}`}
                                onClick={() =>
                                  setFotoAmpliada({ url: fotosBilletes[billete.id], billete })
                                }
                                sx={{
                                  width: 72,
                                  height: 44,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  cursor: 'zoom-in',
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <CameraAlt fontSize="small" color="warning" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={`$${parseFloat(billete.denominacion).toFixed(0)} — Serie: ${billete.numero_serie}`}
                          secondary={[
                            billete.factura ? `Factura: ${billete.factura}` : null,
                            billete.cliente ? `Cliente: ${billete.cliente}` : null,
                          ]
                            .filter(Boolean)
                            .join(' | ') || 'Sin datos adicionales'}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">
                Documentos de respaldo ({detalle.documentos?.length || 0})
              </Typography>
              {detalle.documentos?.length > 0 ? (
                <List dense>
                  {detalle.documentos.map((doc) => (
                    <ListItem
                      key={doc.id}
                      secondaryAction={
                        <Tooltip title="Descargar">
                          <IconButton size="small" onClick={() => handleDescargarDocumento(doc)}>
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemIcon>
                        <Description fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.nombre_original}
                        secondary={`${(doc.tamano / 1024).toFixed(1)} KB`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Este movimiento no tiene documentos de respaldo
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {puedeAnular && detalle?.estado === 'activo' && (
            <Button color="error" onClick={() => setAnular(detalle)}>
              Anular Movimiento
            </Button>
          )}
          <Button
            startIcon={<PictureAsPdf />}
            onClick={() => handleDescargarPdf(detalle)}
          >
            Descargar PDF
          </Button>
          <Button
            startIcon={<Print />}
            onClick={() => handleImprimirPdf(detalle)}
          >
            Imprimir
          </Button>
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* ==================== Diálogo de anulación ==================== */}
      <Dialog open={!!anular} onClose={() => setAnular(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Anular Movimiento #{anular?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            El saldo y el desglose de denominaciones de la caja serán revertidos.
            Esta acción queda registrada en auditoría.
          </Typography>
          <TextField
            label="Motivo de anulación *"
            fullWidth
            multiline
            rows={2}
            value={motivoAnulacion}
            onChange={(e) => setMotivoAnulacion(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAnular(null); setMotivoAnulacion(''); }}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleAnular}>
            Anular
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== Vista previa de foto de billete ==================== */}
      <Dialog open={!!fotoAmpliada} onClose={() => setFotoAmpliada(null)} maxWidth="md">
        <DialogTitle>
          Billete de ${fotoAmpliada ? parseFloat(fotoAmpliada.billete.denominacion).toFixed(0) : ''} —
          Serie: {fotoAmpliada?.billete?.numero_serie}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          {fotoAmpliada && (
            <>
              <Box
                component="img"
                src={fotoAmpliada.url}
                alt={`Billete serie ${fotoAmpliada.billete.numero_serie}`}
                sx={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 1 }}
              />
              {(fotoAmpliada.billete.factura || fotoAmpliada.billete.cliente) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {[
                    fotoAmpliada.billete.factura ? `Factura: ${fotoAmpliada.billete.factura}` : null,
                    fotoAmpliada.billete.cliente ? `Cliente: ${fotoAmpliada.billete.cliente}` : null,
                  ]
                    .filter(Boolean)
                    .join(' | ')}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Download />}
            onClick={() => handleDescargarFotoBillete(fotoAmpliada.billete)}
          >
            Descargar
          </Button>
          <Button onClick={() => setFotoAmpliada(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CajaMovimientosPage;
